import {
    CoreFetch,
    DocumentApi,
    ListProviderDocumentsOrderEnum,
    ListProviderPayersOrderEnum,
    ListSupportDocumentsOrderEnum,
    ListSupportPayersOrderEnum,
    PayerApi,
    ProviderAdjustmentReportItem,
    ProviderDocument,
    ProviderDocumentSortType,
    ProviderPayer,
    ProviderPayerSortType,
    ProviderRemarkReportItem,
    ProviderTransmissionStatusReportItem,
    ReportApi,
    SupportDocumentSortType,
    SupportPayerSortType,
} from '../../api';
import { WorkerConfigurationApiMode } from './worker-configuration.api';
import { getAdjustmentCode } from '../../services/dictionary/adjustment-codes';
import {
    formatPayerAdjustmentGroupLabel,
    normalizePayerAdjustmentGroup,
} from '../../services/dictionary/payer-adjustment-groups';
import { getRemarkCode } from '../../services/dictionary/remark-codes';
import { formatDate } from '../../services/primitives/date-utils';
import { createGroupRequestFilters, createRequestFilters } from '../../ui/data-table/data-table.service';
import { FilterMethod, ICustomFilter } from '../../ui/data-table/components/filters/filters.models';
import { subMonths } from 'date-fns';
import {
    CatalogDocument,
    CatalogPayer,
    CatalogCode,
    CatalogGroup,
    EnterCatalog,
} from './automation/worker-automation.types';

export const CATALOG_REPORT_PERIOD_LABEL = 'from the last month';

const PICKER_SEARCH_LIMIT = 8;

export function getCatalogPayerName(
    catalog: EnterCatalog | null | undefined,
    payerId: string,
    payerById?: Record<string, CatalogPayer>,
): string | undefined {
    return payerById?.[payerId]?.name ?? catalog?.payers.find(payer => payer.payerId === payerId)?.name;
}

export function formatConditionValueLabel(
    kind: 'payerId' | string,
    value: string,
    catalog: EnterCatalog | null | undefined,
    payerById?: Record<string, CatalogPayer>,
): string {
    if (kind === 'payerId') {
        return getCatalogPayerName(catalog, value, payerById) ?? value;
    }

    return value;
}

function humanizeEnumToken(value: string): string {
    return value
        .split('_')
        .filter(Boolean)
        .map(word => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
}

type CatalogPayerSource = Pick<ProviderPayer, 'id' | 'name' | 'nickname' | 'coverage'>;

type CatalogDocumentSource = Pick<ProviderDocument, 'id' | 'filename' | 'description'>;

function formatPayerDisplayName(payer: CatalogPayerSource): string {
    return payer.nickname?.trim() ? `${payer.name} (${payer.nickname})` : payer.name || '';
}

function formatPayerMeta(payer: CatalogPayerSource): string {
    const parts: string[] = [];

    if (payer.coverage) {
        parts.push(humanizeEnumToken(String(payer.coverage)));
    }

    if (payer.id) {
        parts.push(`Payer ID ${payer.id}`);
    }

    return parts.join(' · ');
}

function mapPayersToCatalog(payers: CatalogPayerSource[]): CatalogPayer[] {
    return payers
        .filter(payer => payer.id)
        .map(payer => ({
            payerId: payer.id!,
            name: formatPayerDisplayName(payer),
            meta: formatPayerMeta(payer),
        }))
        .sort((left, right) => left.name.localeCompare(right.name));
}

function mapDocumentsToCatalog(documents: CatalogDocumentSource[]): CatalogDocument[] {
    return documents
        .filter(document => document.id)
        .map(document => ({
            documentId: document.id!,
            name: document.filename || document.description || document.id!,
        }))
        .sort((left, right) => left.name.localeCompare(right.name));
}

async function searchSupportCatalogPayers(query: string, limit: number): Promise<CatalogPayer[]> {
    const term = query.trim();
    const filters = term
        ? createRequestFilters([
              {
                  filterId: 'filterName',
                  filterMethod: FilterMethod.LK,
                  filterType: 'String',
                  value: term,
              },
          ])
        : {};

    const payers = await CoreFetch<PayerApi>(PayerApi).listSupportPayers({
        limit,
        sort: [SupportPayerSortType.NAME],
        order: [ListSupportPayersOrderEnum.ASCENDING],
        filterActive: 'EQ:1',
        ...filters,
    });

    return mapPayersToCatalog(payers);
}

async function searchProviderCatalogPayers(query: string, limit: number): Promise<CatalogPayer[]> {
    const term = query.trim();
    const filters = createRequestFilters(
        term
            ? [
                  {
                      filterId: 'filterName',
                      filterMethod: FilterMethod.LK,
                      filterType: 'String',
                      value: term,
                  },
              ]
            : [],
    );

    const payers = await CoreFetch<PayerApi>(PayerApi).listProviderPayers({
        limit,
        sort: [ProviderPayerSortType.NAME],
        order: [ListProviderPayersOrderEnum.ASCENDING],
        ...filters,
    });

    return mapPayersToCatalog(payers);
}

export async function searchCatalogPayers(
    query: string,
    apiMode: WorkerConfigurationApiMode = 'support',
    limit: number = PICKER_SEARCH_LIMIT,
): Promise<CatalogPayer[]> {
    return apiMode === 'provider'
        ? searchProviderCatalogPayers(query, limit)
        : searchSupportCatalogPayers(query, limit);
}

async function searchSupportCatalogDocuments(query: string, limit: number): Promise<CatalogDocument[]> {
    const term = query.trim();
    const filters = term
        ? createRequestFilters([
              {
                  filterId: 'filterFilename',
                  filterMethod: FilterMethod.LK,
                  filterType: 'String',
                  value: term,
              },
          ])
        : {};

    const documents = await CoreFetch<DocumentApi>(DocumentApi).listSupportDocuments({
        limit,
        sort: [SupportDocumentSortType.CREATED],
        order: [ListSupportDocumentsOrderEnum.DESCENDING],
        ...filters,
    });

    return mapDocumentsToCatalog(documents);
}

async function searchProviderCatalogDocuments(query: string, limit: number): Promise<CatalogDocument[]> {
    const term = query.trim();
    const filters = term
        ? createRequestFilters([
              {
                  filterId: 'filterFilename',
                  filterMethod: FilterMethod.LK,
                  filterType: 'String',
                  value: term,
              },
          ])
        : {};

    const documents = await CoreFetch<DocumentApi>(DocumentApi).listProviderDocuments({
        limit,
        sort: [ProviderDocumentSortType.CREATED],
        order: [ListProviderDocumentsOrderEnum.DESCENDING],
        ...filters,
    });

    return mapDocumentsToCatalog(documents);
}

export async function searchCatalogDocuments(
    query: string,
    apiMode: WorkerConfigurationApiMode = 'support',
    limit: number = PICKER_SEARCH_LIMIT,
): Promise<CatalogDocument[]> {
    return apiMode === 'provider'
        ? searchProviderCatalogDocuments(query, limit)
        : searchSupportCatalogDocuments(query, limit);
}

async function fetchSupportCatalogPayersByIds(ids: string[]): Promise<CatalogPayer[]> {
    const filters: ICustomFilter[][] = [
        ids.map(id => ({
            filterId: 'filterId',
            filterMethod: FilterMethod.EQ,
            filterType: 'String',
            value: id,
        })),
    ];

    const payers = await CoreFetch<PayerApi>(PayerApi).listSupportPayers({
        ...createGroupRequestFilters(filters),
    });

    return mapPayersToCatalog(payers);
}

async function fetchProviderCatalogPayersByIds(ids: string[]): Promise<CatalogPayer[]> {
    const filters: ICustomFilter[][] = [
        ids.map(id => ({
            filterId: 'filterId',
            filterMethod: FilterMethod.EQ,
            filterType: 'String',
            value: id,
        })),
    ];

    const payers = await CoreFetch<PayerApi>(PayerApi).listProviderPayers({
        ...createGroupRequestFilters(filters),
    });

    return mapPayersToCatalog(payers);
}

export async function fetchCatalogPayersByIds(
    ids: string[],
    apiMode: WorkerConfigurationApiMode = 'support',
): Promise<CatalogPayer[]> {
    if (!ids.length) {
        return [];
    }

    return apiMode === 'provider' ? fetchProviderCatalogPayersByIds(ids) : fetchSupportCatalogPayersByIds(ids);
}

function compareCatalogCodes(left: string, right: string): number {
    const leftNumeric = /^\d+$/.test(left);
    const rightNumeric = /^\d+$/.test(right);

    if (leftNumeric && rightNumeric) {
        return Number(left) - Number(right);
    }

    return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
}

function compareCatalogCodesByCount(left: CatalogCode, right: CatalogCode): number {
    return (right.count ?? 0) - (left.count ?? 0) || compareCatalogCodes(left.code, right.code);
}

function catalogReportDateRange(): { end: string; start: string } {
    const end = new Date();
    const start = subMonths(end, 1);

    return {
        start: formatDate(start, 'yyyy-MM-dd'),
        end: formatDate(end, 'yyyy-MM-dd'),
    };
}

function buildCarcCodesFromAdjustmentReport(items: ProviderAdjustmentReportItem[]): CatalogCode[] {
    const counts = new Map<string, number>();

    for (const item of items) {
        if (!item.reasonCode) {
            continue;
        }

        counts.set(item.reasonCode, (counts.get(item.reasonCode) ?? 0) + (item.count ?? 0));
    }

    return [...counts.entries()]
        .map(([code, count]) => ({
            code,
            count,
            desc: getAdjustmentCode(code),
        }))
        .sort(compareCatalogCodesByCount);
}

function buildCarcGroupsFromAdjustmentReport(items: ProviderAdjustmentReportItem[]): CatalogGroup[] {
    const groupCounts = new Map<string, number>();

    for (const item of items) {
        if (!item.group) {
            continue;
        }

        groupCounts.set(item.group, (groupCounts.get(item.group) ?? 0) + (item.count ?? 0));
    }

    return [...groupCounts.entries()]
        .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
        .map(([group, count]) => ({ group: normalizePayerAdjustmentGroup(group), count }));
}

function buildRarcCodesFromRemarkReport(items: ProviderRemarkReportItem[]): CatalogCode[] {
    const counts = new Map<string, number>();

    for (const item of items) {
        if (!item.remarkCode) {
            continue;
        }

        counts.set(item.remarkCode, (counts.get(item.remarkCode) ?? 0) + (item.count ?? 0));
    }

    return [...counts.entries()]
        .map(([code, count]) => ({
            code,
            count,
            desc: getRemarkCode(code),
        }))
        .sort(compareCatalogCodesByCount);
}

function buildRejectionStatusesFromTransmissionReport(items: ProviderTransmissionStatusReportItem[]): CatalogCode[] {
    return items
        .filter(item => item.message)
        .map(item => ({
            code: item.message!,
            count: item.count ?? 0,
            desc: item.message!,
        }))
        .sort(compareCatalogCodesByCount);
}

export async function fetchEnterCatalog(): Promise<EnterCatalog> {
    const range = catalogReportDateRange();
    const reportApi = CoreFetch<ReportApi>(ReportApi);

    const [adjustments, remarks, transmissionStatuses] = await Promise.all([
        reportApi.getAdjustmentReport(range),
        reportApi.getRemarkReport(range),
        reportApi.getTransmissionStatusReport(range),
    ]);

    return {
        carcCodes: buildCarcCodesFromAdjustmentReport(adjustments),
        carcGroups: buildCarcGroupsFromAdjustmentReport(adjustments),
        rarcCodes: buildRarcCodesFromRemarkReport(remarks),
        rejectionStatuses: buildRejectionStatusesFromTransmissionReport(transmissionStatuses),
        payers: [],
        documents: [],
    };
}

export function formatCatalogSyncLabel(date: Date = new Date()): string {
    return `Loaded ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

export function formatCatalogCountLabel(catalog: EnterCatalog): string {
    return `${catalog.carcCodes.length} CARC · ${catalog.rarcCodes.length} RARC · ${catalog.carcGroups.length} CARC groups · ${catalog.rejectionStatuses.length} transmission statuses`;
}

export function formatCatalogGroupLabel(group: string): string {
    return formatPayerAdjustmentGroupLabel(group);
}
